import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useOrganizationTeacherGetOrganizationSettings } from "@/orval/organization-teacher";
import {
  getTeacherClassesGetTeacherClassesDataTableQueryKey,
  teacherClassesGetTeacherClassesDataTable,
} from "@/orval/teacher-classes";
import type { TeacherClassListItemDTO } from "@/orval/model";
import { Form } from "@/components/form/form";
import { OrganizationSemestersComboboxFormField } from "@/components/form/form-field/combobox/organization-semesters-combobox-form-field";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DownloadIcon } from "lucide-react";
import { format, parse } from "date-fns";
import { PortalPageHeader } from "@/components/portal-page-header";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { useAuth } from "@/hooks/auth/auth";
import type { CustomColumnDef } from "@/types/custom-column-def";
import { optionalSemesterSearchSchema } from "@/lib/route-search";
import { downloadAuthenticatedFile } from "@/lib/download-file";
import { useState } from "react";
import { toast } from "sonner";

const timeParseBaseDate = new Date(0);
const blockedRosterMessage = "Roster access unlocks after registration closes for this semester.";

function formatClassTime(time: string) {
  return format(parse(time, "HH:mm:ss", timeParseBaseDate), "h:mm a");
}

export const Route = createFileRoute("/_app/organizations/$organizationId/teacher/classes")({
  validateSearch: optionalSemesterSearchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const { organizationId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [exportingClassId, setExportingClassId] = useState<number | null>(null);

  const { data: teacherSettings } = useOrganizationTeacherGetOrganizationSettings(organizationId);

  const semesterId =
    search.semesterId ?? teacherSettings?.activeSemesterId ?? teacherSettings?.nextSemesterId ?? undefined;

  const exportRoster = async (classId: number) => {
    if (!token) {
      toast.error("Sign in again to export the roster.");
      return;
    }

    setExportingClassId(classId);

    try {
      await downloadAuthenticatedFile({
        url: `/api/organizations/${organizationId}/teacher/TeacherClasses/${classId}/export`,
        token,
        fallbackFilename: "class_roster.xlsx",
      });
    } catch (error) {
      console.error("Error exporting roster:", error);
      toast.error("Unable to export roster.");
    } finally {
      setExportingClassId(null);
    }
  };

  const columns: CustomColumnDef<TeacherClassListItemDTO>[] = [
    {
      header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
      accessorKey: "name",
      id: "Name",
    },
    {
      header: ({ column }) => <DataTableColumnHeader column={column} title="Day" />,
      accessorKey: "classDayName",
      id: "ClassDayName",
    },
    {
      header: ({ column }) => <DataTableColumnHeader column={column} title="Time" />,
      accessorFn: (row) => `${formatClassTime(row.startTime)} – ${formatClassTime(row.endTime)}`,
      id: "StartTime",
    },
    {
      header: ({ column }) => <DataTableColumnHeader column={column} title="Enrolled" />,
      accessorFn: (row) => `${row.enrolledStudentsCount}${row.studentLimit != null ? ` / ${row.studentLimit}` : ""}`,
      id: "EnrolledStudentsCount",
    },
    {
      header: "Roster Access",
      id: "rosterAccess",
      enableSorting: false,
      cell: ({ row }) =>
        row.original.isRosterAccessBlocked ? (
          <div className="flex flex-col gap-1">
            <Badge variant="secondary" className="w-fit">
              Locked
            </Badge>
          </div>
        ) : (
          <Badge variant="outline" className="w-fit">
            Available
          </Badge>
        ),
    },
    {
      header: "Export Roster",
      id: "exportRoster",
      enableSorting: false,
      size: 80,
      cell: ({ row }) =>
        row.original.isRosterAccessBlocked ? (
          <Button size="sm" variant="outline" disabled title={blockedRosterMessage}>
            <DownloadIcon className="size-4" />
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            title="Export roster to Excel"
            disabled={exportingClassId === row.original.id}
            onClick={(e) => {
              e.stopPropagation();
              void exportRoster(row.original.id);
            }}
          >
            <DownloadIcon className="size-4" />
          </Button>
        ),
    },
  ];

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader
        className="mb-8"
        title="My Classes"
        subtitle="View assigned classes and export rosters when registration has closed"
        actions={
          <Form<{ semesterId?: number }>
            key={semesterId ?? "no-semester"}
            useFormProps={{ defaultValues: { semesterId } }}
            onChange={(data) => navigate({ to: ".", search: { semesterId: data.semesterId } })}
            containerClassName="w-full space-y-0 md:w-80"
          >
            <OrganizationSemestersComboboxFormField
              className="w-full"
              name="semesterId"
              organizationId={organizationId}
            />
          </Form>
        }
      />

      <DataTable
        columns={columns}
        queryFn={(queryOptions, signal) => {
          if (!semesterId) {
            return Promise.resolve({ items: [], totalCount: 0, totalPages: 0 });
          }

          return teacherClassesGetTeacherClassesDataTable(organizationId, { ...queryOptions, semesterId }, signal);
        }}
        queryKeyFn={(queryOptions) =>
          getTeacherClassesGetTeacherClassesDataTableQueryKey(organizationId, { ...queryOptions, semesterId })
        }
        getRowIdFn={(row) => row.id.toString()}
        rowLink={(row) => ({
          to: "/organizations/$organizationId/teacher/classes/$classId",
          params: { organizationId, classId: row.id },
        })}
        missingRequiredFilterValues={!semesterId}
        missingRequiredFilterValuesMessage="Select a semester to view your classes."
      />
    </div>
  );
}
